import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Card, CardContent } from "../ui/card";
import { getPortalAssessments } from "../../lib/api";

const CATS = [
  ["participation_level", "Participation"],
  ["engagement", "Engagement"],
  ["communication", "Communication"],
  ["behavior", "Behavior"],
  ["attention", "Attention"],
  ["work_completion", "Work Completion"],
];

const avgColor = (v) => {
  if (v == null) return "text-slate-400";
  if (v >= 3) return "text-emerald-600";
  if (v >= 2) return "text-amber-600";
  return "text-red-600";
};

export default function PortalAssessments() {
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getPortalAssessments(start, end);
      setRows(res.data.students || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load assessments");
    } finally { setLoading(false); }
  }, [start, end]);

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-4" data-testid="portal-assessments">
      <Card>
        <CardContent className="py-4 flex flex-wrap items-end gap-3">
          <div>
            <label className="text-xs text-muted-foreground block mb-1">From (optional)</label>
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="w-44" data-testid="portal-assess-start" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">To (optional)</label>
            <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="w-44" data-testid="portal-assess-end" />
          </div>
          <Button onClick={load} disabled={loading} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="portal-assess-run">
            {loading ? "Loading..." : "View"}
          </Button>
        </CardContent>
      </Card>

      {loading ? (
        <Card><CardContent className="py-16 text-center text-muted-foreground">Loading…</CardContent></Card>
      ) : rows.length === 0 ? (
        <Card><CardContent className="py-16 text-center text-muted-foreground" data-testid="portal-assess-empty">No students found for this district.</CardContent></Card>
      ) : (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm" data-testid="portal-assess-table">
              <thead>
                <tr className="border-b bg-slate-50 text-left">
                  <th className="p-3">Student</th>
                  <th className="p-3 text-center">Entries</th>
                  <th className="p-3 text-center">DNA</th>
                  {CATS.map(([k, label]) => (
                    <th key={k} className="p-3 text-center whitespace-nowrap">{label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((s) => (
                  <tr key={s.student_id} className="border-b hover:bg-slate-50" data-testid={`portal-assess-row-${s.student_id}`}>
                    <td className="p-3 font-medium whitespace-nowrap">{s.student_name}</td>
                    <td className="p-3 text-center">{s.scored_entries}/{s.total_entries}</td>
                    <td className="p-3 text-center">{s.dna_entries}</td>
                    {CATS.map(([k]) => (
                      <td key={k} className={`p-3 text-center font-semibold ${avgColor(s.averages?.[k])}`}>
                        {s.averages?.[k] == null ? "—" : s.averages[k]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
      <p className="text-xs text-muted-foreground">Scores are averaged on a 1–4 scale (higher is better). DNA = did not attend.</p>
    </div>
  );
}
